import React, { useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { SPOTIFY_CANVAS_TERMS_TEXT, SPOTIFY_FOR_ARTISTS_TERMS_URL } from 'fym-shared/src/music24/spotifyCanvas';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogTileMark } from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { IntegrationTypeIcon } from '../../components/IntegrationTypeIcon/IntegrationTypeIcon';
import { spotifyCanvasMessages, spotifyCanvasSwitchMessages as m } from './spotifyCanvasMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Canvas row; each part uses its own slice of it */
import styles from './SpotifyCanvas.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** `autoUpload`: opened by the switch (b3). `upload`: opened by a click upload or a 409 (b4). */
export type CanvasTermsMode = 'autoUpload' | 'upload';

interface SpotifyCanvasTermsDialogProps {
  isOpen: boolean;
  mode: CanvasTermsMode;
  /** The Music24 artist ("Accept one time for LaziiBee"). */
  artistName: string;
  /** The song of a click upload ("the Canvas loop of Swamps"). */
  songTitle?: string;
  isBusy: boolean;
  /** The PUT or the POST of the accept failed: the dialog stays open with the sentence. */
  failed: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

/**
 * The Spotify Canvas terms (design spotify-canvas b3, b4): the notice of Spotify for Artists word for
 * word (`SPOTIFY_CANVAS_TERMS_TEXT`, the version the API stores), the box "I accept" and the link to
 * the terms. The main button stays off until the box is ticked.
 */
const SpotifyCanvasTermsDialogComponent: React.FC<SpotifyCanvasTermsDialogProps> = ({
  isOpen,
  mode,
  artistName,
  songTitle,
  isBusy,
  failed,
  onAccept,
  onCancel,
}) => {
  const intl = useIntl();
  const checkboxId = useId();
  const [accepted, setAccepted] = useState(false);
  // Every open starts with the box not ticked.
  useEffect(() => {
    if (isOpen) {
      setAccepted(false);
    }
  }, [isOpen]);

  const what =
    mode === 'upload'
      ? intl.formatMessage(m.termsWhatUpload, { title: songTitle ?? '' })
      : intl.formatMessage(m.explanation);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      mark={
        <ConfirmDialogTileMark>
          <IntegrationTypeIcon integrationType={MusicAPIIntegrationID.Spotify} size={40} />
        </ConfirmDialogTileMark>
      }
      title={intl.formatMessage(m.termsTitle)}
      description={intl.formatMessage(m.termsSub, { artist: artistName })}
      confirmLabel={intl.formatMessage(mode === 'upload' ? m.termsUpload : m.termsTurnOn)}
      isBusy={isBusy}
      isConfirmDisabled={!accepted}
      error={failed ? intl.formatMessage(spotifyCanvasMessages.didNotWork) : undefined}
      onConfirm={onAccept}
      onCancel={onCancel}
      testId="spotify-canvas-terms"
    >
      <span className={styles.termsLabel}>{intl.formatMessage(m.termsLabel)}</span>
      {/* Spotify's own notice: the API stores the accept of this exact text (SPOTIFY_CANVAS_TERMS_VERSION). */}
      <p className={styles.terms}>{SPOTIFY_CANVAS_TERMS_TEXT}</p>
      <div className={styles.accept}>
        <label htmlFor={checkboxId}>
          <input
            id={checkboxId}
            type="checkbox"
            checked={accepted}
            disabled={isBusy}
            onChange={(event) => setAccepted(event.target.checked)}
          />
          {intl.formatMessage(m.termsAccept)}
        </label>
        <a href={SPOTIFY_FOR_ARTISTS_TERMS_URL} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage(m.termsLink)} &#8599;
        </a>
      </div>
      <p className={styles.what}>{withBrandWord(what)}</p>
    </ConfirmDialog>
  );
};

export const SpotifyCanvasTermsDialog = TypedMemo(SpotifyCanvasTermsDialogComponent);
